// * Importando o React
import { useState, useCallback, useEffect, useMemo } from "react";

// * Importing the react-select
import AsyncSelect from "react-select/async";

// * Importing the react-bootstrap
import { Form, Button, Modal, Row, Col } from "react-bootstrap";

// * Importing the react-router
import { useNavigate, useParams } from "react-router-dom";

// * Importing the react-transition-group
import { CSSTransition, TransitionGroup } from "react-transition-group";

// * Importing the react-icons
import { RiArrowGoBackLine } from "react-icons/ri";

//? Importing the component
import DefaultButton from "../../../../component/base/DefaultButton";
import { Attention } from "../../../../component/base/Attention";
import { Searchbar } from "../../../../component/list/searchbar";
import { Actions } from "../../../../component/list/actions";

// * Importing the useTeachers and useReviewers hook
import { useTeachers } from "../../../../hooks/teachers/useTeachers";
import { useReviewers } from "../../../../hooks/applications/Reviewers/useReviewers";

/**
 * Stundent type
 */
type SelectedTeacher = {
  id: string;
  name: string;
  email: string;
};

/**
 * New reviewer type
 */
type ExistingReviewerTeacher = {
  reviewerId: string;
  userId: string;
  name: string;
  email: string;
};

/**
 * Component to render selected teachers
 */
function SelectedTeachersTable({
  selectedTeachers,
  searchText,
  onRemoveTeacher,
}: {
  selectedTeachers: SelectedTeacher[];
  searchText: string;
  onRemoveTeacher: (teacherId: string) => void;
}) {
  // Filtra a lista memoizada
  const filteredTeachers = useMemo(() => {
    const lowerSearch = searchText.toLowerCase();
    return selectedTeachers.filter(
      (t) =>
        t.name.toLowerCase().includes(lowerSearch) ||
        t.email.toLowerCase().includes(lowerSearch)
    );
  }, [selectedTeachers, searchText]);

  if (!selectedTeachers.length) {
    return (
      <div className="table-responsive mt-2">
        <table className="table table-striped">
          <tbody>
            <tr>
              <td colSpan={3} style={{ textAlign: "center" }}>
                Nenhum professor selecionado
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <TransitionGroup component={null}>
      <CSSTransition timeout={300} classNames="fade">
        <div className="table-responsive mt-2">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredTeachers.map((teacher) => (
                <CSSTransition key={teacher.id} timeout={300} classNames="fade">
                  <tr>
                    <td>{teacher.name}</td>
                    <td>{teacher.email}</td>
                    <td>
                      <Actions
                        actionItem={teacher}
                        onDeleteClick={() => onRemoveTeacher(teacher.id)}
                        hideManager
                        hideEdit
                      />
                    </td>
                  </tr>
                </CSSTransition>
              ))}
            </tbody>
          </table>
        </div>
      </CSSTransition>
    </TransitionGroup>
  );
}

/**
 * Componente para renderizar revisores já existentes
 */
function ExistingReviewersTable({
  existingReviewerTeachers,
  onRemoveExistingReviewer,
}: {
  existingReviewerTeachers: ExistingReviewerTeacher[];
  onRemoveExistingReviewer: (reviewerId: string) => void;
}) {
  if (!existingReviewerTeachers.length) return null;

  return (
    <div className="mt-4">
      <h5>Revisores já adicionados:</h5>
      <div className="table-responsive mt-2">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Nome</th>
              <th>E-mail</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {existingReviewerTeachers.map((teacher) => (
              <tr key={teacher.reviewerId}>
                <td>{teacher.name}</td>
                <td>{teacher.email}</td>
                <td>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => onRemoveExistingReviewer(teacher.reviewerId)}
                  >
                    Remover Revisor
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Componente Principal
 */
export function ApplicationManagerFormReviewers() {
  const navigate = useNavigate();
  const params = useParams();

  const applicationId = params.id || "";
  const token = localStorage.getItem("token") || "";

  const [selectedTeachers, setSelectedTeachers] = useState<SelectedTeacher[]>(
    []
  );
  const [selectedTeacherOptions, setSelectedTeacherOptions] = useState<
    SelectedTeacher[]
  >([]);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [searchText, setSearchText] = useState("");

  const handleCloseDuplicateAlert = useCallback(
    () => setShowDuplicateAlert(false),
    []
  );

  const { teachers, loading: loadingTeachers } = useTeachers(token);
  const {
    reviewers,
    createReviewer,
    deleteReviewer,
    loading: loadingReviewers,
  } = useReviewers(applicationId, token);

  // Mapeia revisores existentes
  const existingReviewerTeachers: ExistingReviewerTeacher[] = useMemo(() => {
    return reviewers.map((r) => ({
      reviewerId: r.id,
      userId: r.user?.id || "",
      name: r.user?.name || "",
      email: r.user?.email || "",
    }));
  }, [reviewers]);

  // Função para carregar professores para o AsyncSelect
  const loadTeachers = useCallback(
    async (inputValue: string) => {
      const lowerSearch = inputValue.toLowerCase();
      const filtered = teachers.filter(
        (teacher) =>
          teacher.name.toLowerCase().includes(lowerSearch) ||
          teacher.email.toLowerCase().includes(lowerSearch)
      );
      // Limita a 5 resultados
      return filtered.slice(0, 5).map((t) => ({
        id: t.id,
        name: t.name,
        email: t.email,
      }));
    },
    [teachers]
  );

  // Adicionar novo professor
  const handleAddTeacherIndividually = useCallback(
    (newTeachers: SelectedTeacher[]) => {
      const duplicates = newTeachers.filter(
        (newT) =>
          selectedTeachers.some((selT) => selT.email === newT.email) ||
          existingReviewerTeachers.some((exT) => exT.email === newT.email)
      );
      if (duplicates.length > 0) {
        setShowDuplicateAlert(true);
      }

      const uniqueNew = newTeachers.filter(
        (cand) =>
          !selectedTeachers.some((sel) => sel.email === cand.email) &&
          !existingReviewerTeachers.some((ex) => ex.email === cand.email)
      );
      setSelectedTeachers((prev) => [...prev, ...uniqueNew]);
      setSelectedTeacherOptions([]);
    },
    [selectedTeachers, existingReviewerTeachers]
  );

  // Remove professor da lista local
  const handleRemoveTeacher = useCallback((teacherId: string) => {
    setSelectedTeachers((prev) => prev.filter((t) => t.id !== teacherId));
  }, []);

  // Remove um revisor da aplicação (chamada na API)
  const handleRemoveExistingReviewer = useCallback(
    async (reviewerId: string) => {
      try {
        await deleteReviewer(reviewerId);
      } catch (err) {
        console.error("Erro ao excluir revisor:", err);
      }
    },
    [deleteReviewer]
  );

  // Salvar revisores
  const handleSave = useCallback(async () => {
    try {
      for (const teacher of selectedTeachers) {
        if (
          !existingReviewerTeachers.some((ex) => ex.email === teacher.email)
        ) {
          await createReviewer({ teacher_id: teacher.id });
        }
      }
      navigate(`/tests/view/${applicationId}`);
    } catch (err) {
      console.error("Erro ao salvar professores (reviewers):", err);
    }
  }, [
    selectedTeachers,
    createReviewer,
    applicationId,
    navigate,
    existingReviewerTeachers,
  ]);

  // Mantém o valor selecionado no AsyncSelect e remove caso seja excluído depois
  useEffect(() => {
    setSelectedTeacherOptions((prev) =>
      prev.filter((sel) => selectedTeachers.some((t) => t.id === sel.id))
    );
  }, [selectedTeachers]);

  return (
    <div className="row">
      {/* Modal de duplicados */}
      <Modal show={showDuplicateAlert} onHide={handleCloseDuplicateAlert}>
        <Modal.Header>
          <Modal.Title>Atenção</Modal.Title>
        </Modal.Header>
        <Modal.Body>Este professor já foi adicionado anteriormente.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDuplicateAlert}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <h4
              className="mt-0 header-title"
              style={{ fontSize: "1.5rem", color: "black" }}
            >
              Adicionar/Remover Revisores (Professores)
            </h4>
            <p style={{ fontSize: "1rem", color: "black" }}>
              Cadastre/Edite os professores como Revisores da aplicação
            </p>

            <Form
              onSubmit={(e) => {
                e.preventDefault();
                void handleSave();
              }}
            >
              <Row className="mb-4 align-items-end">
                <Col md={12}>
                  <Form.Group controlId="addTeacher">
                    <Form.Label>
                      Adicionar Professor Individualmente <Attention />
                    </Form.Label>
                    <AsyncSelect<SelectedTeacher, true>
                      isMulti
                      cacheOptions
                      defaultOptions={teachers.map((t) => ({
                        id: t.id,
                        name: t.name,
                        email: t.email,
                      }))}
                      loadOptions={loadTeachers}
                      onChange={(value) => {
                        if (value) {
                          handleAddTeacherIndividually(
                            value as SelectedTeacher[]
                          );
                        }
                      }}
                      getOptionLabel={(option) =>
                        `${option.name} (${option.email})`
                      }
                      getOptionValue={(option) => option.id}
                      placeholder="Digite para buscar professores..."
                      value={selectedTeacherOptions}
                      isDisabled={loadingTeachers}
                      styles={{
                        container: (base) => ({ ...base, width: "100%" }),
                        control: (base) => ({ ...base, minHeight: "38px" }),
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>
                  Professores Selecionados <Attention />
                </Form.Label>
                <Searchbar
                  search_input_value={searchText}
                  search_text="Filtrar por nome ou e-mail"
                  onChangeSearchInput={setSearchText}
                  onSearchButtonClick={() => undefined}
                />
              </Form.Group>

              {/* Tabela de professores selecionados */}
              <SelectedTeachersTable
                selectedTeachers={selectedTeachers}
                searchText={searchText}
                onRemoveTeacher={handleRemoveTeacher}
              />

              {/* Tabela de revisores existentes */}
              <ExistingReviewersTable
                existingReviewerTeachers={existingReviewerTeachers}
                onRemoveExistingReviewer={handleRemoveExistingReviewer}
              />

              <div className="mt-4 d-flex justify-content-between">
                <DefaultButton
                  bg="secondary"
                  text="Voltar"
                  icon={<RiArrowGoBackLine />}
                  to={`/tests/view/${applicationId}`}
                  disabled={loadingReviewers}
                />
                <DefaultButton
                  type="submit"
                  bg="confirm"
                  text="Salvar Revisores"
                  loadingtext="Salvando..."
                  loading={loadingReviewers}
                  disabled={loadingReviewers}
                />
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
